import { useCallback, useEffect, useRef, useState } from "react";
import HudPanel from "./HudPanel.jsx";
import useHudLayout from "./useHudLayout.js";
import "./hud.css";

/**
 * HudCanvas — an Illustrator / game-HUD style workspace overlaid on the map.
 *
 * Usage:
 *   <HudCanvas panels={panels} storageKey="hud-v1" />
 *
 * panels = [{ id, title, node, defaultRect:{x,y,w,h}, defaultOpen? }]
 *
 * The overlay itself is pointer-events:none so the map stays interactive;
 * each panel and the toolbar re-enable pointer-events:auto.
 */
export default function HudCanvas({ panels = [], storageKey = "hud-v1" }) {
  const [layout, setPanel, resetLayout, visible, toggleVisible] = useHudLayout(
    storageKey,
    panels
  );

  const [locked, setLocked] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Per-panel z-order. Highest wins. Bumped on pointer-down.
  const [order, setOrder] = useState({});
  const zTop = useRef(10);
  const bringToFront = useCallback((id) => {
    setOrder((prev) => {
      const next = (zTop.current += 1);
      if (prev[id] === next) return prev;
      return { ...prev, [id]: next };
    });
  }, []);

  // Close the widgets menu on outside click / Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  return (
    <div className="hud-canvas" aria-hidden={false}>
      {/* Always-visible toolbar */}
      <div className="hud-toolbar" role="toolbar" aria-label="HUD controls">
        <div className="hud-menu-wrap" ref={menuRef}>
          <button
            type="button"
            className="hud-btn"
            aria-haspopup="true"
            aria-expanded={menuOpen}
            aria-label="Add or toggle widgets"
            onClick={() => setMenuOpen((o) => !o)}
          >
            ＋ Widgets
          </button>
          {menuOpen && (
            <div className="hud-menu" role="menu" aria-label="Widgets">
              {panels.map((p) => (
                <label className="hud-menu-item" key={p.id} role="menuitemcheckbox" aria-checked={!!visible[p.id]}>
                  <input
                    type="checkbox"
                    checked={!!visible[p.id]}
                    onChange={() => toggleVisible(p.id)}
                  />
                  <span>{p.title}</span>
                </label>
              ))}
              {panels.length === 0 && (
                <div className="hud-menu-empty">No widgets</div>
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          className={`hud-btn${locked ? " is-active" : ""}`}
          aria-pressed={locked}
          aria-label={locked ? "Unlock panel editing" : "Lock panel editing"}
          onClick={() => setLocked((l) => !l)}
        >
          {locked ? "🔒 Locked" : "🔓 Edit"}
        </button>

        <button
          type="button"
          className="hud-btn"
          aria-label="Reset layout to defaults"
          onClick={resetLayout}
        >
          ↺ Reset
        </button>
      </div>

      {/* Panels */}
      {panels.map((p) => {
        const rect = layout[p.id];
        if (!rect || !rect.open) return null;
        return (
          <HudPanel
            key={p.id}
            id={p.id}
            title={p.title}
            rect={rect}
            collapsed={!!rect.collapsed}
            locked={locked}
            z={order[p.id] || 1}
            onFocus={() => bringToFront(p.id)}
            onChange={(patch) => setPanel(p.id, patch)}
            onClose={() => toggleVisible(p.id, false)}
          >
            {p.node}
          </HudPanel>
        );
      })}
    </div>
  );
}
